import {
  IndexedInteractionStepResponse,
  MultiTurnInteractionConfiguration,
} from '@scale/shared/types/chat';
import { isNil } from 'lodash';

/*
 * Matches the inline validation logic in EditableChatMessage and InteractionInput
 */
/* eslint-disable complexity */
export function validateRequiredReferenceTextsAndAttachments(
  response: IndexedInteractionStepResponse,
  config: MultiTurnInteractionConfiguration,
  turnIndex: number, // treated as a 0-indexed representation of the turn number
) {
  const submitTooltip = [];
  let canSubmit = true;

  const isMultiTurnStep = turnIndex > 0;

  // Validate reference texts
  const referenceTexts = response.context.referenceTexts;

  const enableAttachments =
    config.useAttachments && (!isMultiTurnStep || (config.useAttachmentsOnMultiTurn ?? true));
  const minReferenceTexts = config.minReferenceTexts ?? (config.isReferenceTextRequired ? 1 : 0);
  const isReferenceTextRequiredOnTurn = !(
    isMultiTurnStep && config.requireReferenceTextOnlySingleTurn
  );
  const referenceTextsRequired =
    enableAttachments && minReferenceTexts > 0 && isReferenceTextRequiredOnTurn;

  if (referenceTextsRequired && (!referenceTexts || referenceTexts.length < minReferenceTexts)) {
    submitTooltip.push(`At least ${minReferenceTexts} reference text(s) are required.`);
    canSubmit = false;
  }

  // Validate attachments / images
  const attachments = response.context.attachments;

  const enableImageUrls =
    config.useImageUrls && (!isMultiTurnStep || config.useImageUrlsOnMultiTurn);
  const enableMmImageService =
    config.enableMmImageService && (!isMultiTurnStep || config.useImageUrlsOnMultiTurn);
  const enableGeneralAttachments =
    config.useAttachments && (!isMultiTurnStep || (config.useAttachmentsOnMultiTurn ?? true));

  const isAttachmentRequiredOnTurn = !(isMultiTurnStep && config.requireImageUrlsOnlySingleTurn);
  const imagesRequired =
    (enableImageUrls || enableMmImageService) &&
    config.isImageUrlRequired &&
    isAttachmentRequiredOnTurn;

  const generalAttachmentsRequired = enableGeneralAttachments && config.isAttachmentRequired;
  const attachmentsRequired = generalAttachmentsRequired || imagesRequired;

  if (attachmentsRequired && (!attachments || attachments.length === 0)) {
    submitTooltip.push('Please provide an attachment.');
    canSubmit = false;
  }

  if (
    enableAttachments &&
    !isNil(config.minAttachments) &&
    config.minAttachments > (attachments?.length ?? 0)
  ) {
    submitTooltip.push(`Please provide at least ${config.minAttachments} attachment(s).`);
    canSubmit = false;
  }

  if (
    enableAttachments &&
    !isNil(config.maxAttachments) &&
    config.maxAttachments < (attachments?.length ?? 0)
  ) {
    submitTooltip.push(`Please provide at most ${config.maxAttachments} attachment(s).`);
    canSubmit = false;
  }

  return { canSubmit, submitTooltip: submitTooltip.join(' ') };
}
